/**
 * The Plant Brief (Foundry Intelligence, stage 1c; docs/plans/2026-09-26_foundry-intelligence.md): the typed contract
 * of `GET /v1/twin/analysis/brief` and its hook. The engine composes one document from the org's latest done
 * analysis run (pinned; its result ids cited), the lots rollup, the priors, the playbook, the planner and the groups;
 * every number the page prints is in it, and `facts` holds each one for the consultant's number check.
 *
 *   GET /v1/twin/analysis/brief   the brief of the latest done run
 *     404 brief_not_found         no analysis run has finished for this org yet
 *     404 (any other)             this platform does not serve the brief yet (the route is not mounted)
 *
 * The briefs at every cadence ("Update 2026-09-26 (evening)" in the plan): one CadenceBrief per closed shift, day,
 * week, month and year, stored in plant_briefs (0182). The Go mirror is platform-core/internal/twin/
 * brief_cadence_types.go; change the two together.
 *
 *   GET /v1/twin/briefs?cadence=<c>&limit=60        the cadence's briefs, newest first
 *   GET /v1/twin/briefs/{cadence}/{period_key}      one brief (the PlantBrief for overall/all)
 *     404 brief_not_found                           no brief for that period yet
 *     404 (any other)                               this platform does not serve the briefs yet
 *
 * The consultant's summary of a stored brief (Foundry Intelligence 3, #1018): a headline and a few paragraphs the brain
 * writes from the brief's document alone, each sentence citing the brief's facts its numbers come from.
 *
 *   GET /v1/twin/briefs/{cadence}/{period_key}/narrative   ready, pending, failed or none (overall/all: the overall one)
 *     404 brief_not_found                                  no brief for that period: no summary
 *     404 (any other)                                      the brain's worker token is not set, so the route is not
 *                                                          mounted: no summary either, never an error on the page
 *
 * Association, not cause: a verdict says how well the plant's data bears out a textbook mechanism, never that the
 * condition caused the scrap. A cadence brief's signal says "watch this": its mechanisms and their standing come
 * from the long-run analysis behind the overall brief.
 */
import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiClientError } from "@/lib/api-client";
import { useAuth } from "@/lib/auth";
import { useOrg } from "@/lib/org-context";
import { plantTimeApi } from "@/lib/shift-calendar";
import type { MachineEntry } from "./equipment-summary";
import type { RecommendationStates, RecommendationVerdicts } from "./recommendations";

/** How well the plant's data bears out a mechanism (the engine's rule, deterministic). */
export type BriefVerdict = "supported" | "suggestive" | "not_supported" | "untestable";

/** How an evidence item was tested: a condition's window, a straight association, a factor, a setpoint's tracking. */
export type BriefTest = "window" | "association" | "factor" | "setpoint";

/** Why a mechanism cannot be tested yet (the planner's and the groups' reasons). */
export type BriefBlockReason =
  | "no_input"
  | "lag_unknown"
  | "no_path"
  | "ambiguous_sample"
  | "too_few_lots"
  | "no_contrast"
  | "withheld";

/** A plant-day window, YYYY-MM-DD on the plant clock, both ends included. */
export interface BriefWindow {
  from: string;
  to: string;
}

export interface BriefLotsWindow extends BriefWindow {
  lots: number;
  /** Lots held out of every figure (scrap above castings, scrap on no castings, negative bookings). */
  held_excluded: number;
}

export interface BriefProcessWindow extends BriefWindow {
  heats: number;
}

export interface BriefPeriod {
  /** "2021", "2026 to date", or a plant month "2026-05". */
  period: string;
  cast: number;
  scrap: number;
  /** scrap / cast, a fraction; null when nothing was cast (never 0). */
  rate: number | null;
}

export interface BriefParetoRow {
  /** The taxonomy defect id (the org's label maps to it through its vocabulary). */
  defect: string;
  /** The plant's own word for it. */
  label: string;
  pieces: number;
  /** Share of all scrap pieces in the lots window, a fraction. */
  share: number;
  /** Where the Pareto is ranked by money (brief/v16): what its pieces cost; absent where none could be priced. */
  cost?: number;
  /** Where the Pareto is ranked by money: the pieces it could price. */
  priced_pieces?: number;
}

/**
 * The latest year to date against the same span of the year before (brief/v12): 1 January to the lots window's last
 * plant day, against 1 January to the same plant day a year earlier. Each span's castings, scrap and rate, and each
 * Pareto defect's pieces and rate of that span's castings.
 */
export interface BriefToDate {
  from: string;
  to: string;
  prior_from: string;
  prior_to: string;
  cast: number;
  scrap: number;
  rate: number | null;
  prior_cast: number;
  prior_scrap: number;
  prior_rate: number | null;
  defects: {
    defect: string;
    label: string;
    pieces: number;
    rate: number | null;
    prior_pieces: number;
    prior_rate: number | null;
  }[];
  /** The defects with pieces in either span past those listed. */
  more: number;
}

/**
 * One of the Pareto's largest defects by the part its pieces were booked on, over the lots window (brief/v11): the parts
 * that booked the most of its pieces, most first, and how many more parts booked it. Each row's numbers are facts,
 * parts.<defect>.<i>.pieces, .share, .cast and .rate (i from 1).
 */
export interface BriefDefectParts {
  defect: string;
  parts: {
    /** The plant's own word for the part. */
    part: string;
    pieces: number;
    /**
     * Share of the defect's pieces in the lots window, a fraction; null where the part booked more of the defect than
     * the defect nets (a reversal booked on another part).
     */
    share: number | null;
    /** The part's own castings in the lots window. */
    cast: number;
    /** The pieces over the part's own castings in the window; null under 100 castings, too few for a rate. */
    rate: number | null;
  }[];
  more: number;
}

export type BriefChange =
  | {
      kind: "level";
      /** null: the plant's whole scrap rate. */
      defect: string | null;
      from: { period: string; rate: number };
      to: { period: string; rate: number };
      fact_keys: string[];
    }
  | {
      kind: "trend";
      defect: string | null;
      /** The monthly trend family's Sen slope (a fraction of castings per month in the run), times 12. */
      sen_slope_per_year: number;
      /**
       * The Mann-Kendall p of the trend (its sibling mann_kendall row). The monthly trend family has no
       * Benjamini-Hochberg q: BH skips it, so a trend never carries one.
       */
      p: number;
      result_id: string;
      /** Every row the trend states: the Sen row its slope comes from, then the Mann-Kendall row its p does. */
      result_ids?: string[];
      /** The facts of its numbers: its slope's, then its p's. */
      fact_keys?: string[];
    };

/** A playbook mechanism, as the engine hands it over for one defect (packs/foundry/analysis/playbook.yaml). */
export interface BriefMechanism {
  id: string;
  title: string;
  explain: string;
  check: string[];
  actions: string[];
  /**
   * Each action's recommendation key, in its order (brief/v9): the key a period brief gives the same playbook action,
   * so what the plant did with it shows on both. Absent in a brief composed before.
   */
  action_keys?: string[];
  settles: string[];
}

export interface BriefCondition {
  key: string;
  label: string;
  unit: string;
}

export interface BriefEffect {
  /** Window tests: the defect's rate in the lots inside and outside the condition's window, fractions. */
  rate_in?: number;
  rate_out?: number;
  rate_out_ci?: [number, number];
  /** Window tests only: (rate outside − rate inside) × castings outside, an association-level estimate. */
  excess_pieces?: number;
  excess_pieces_ci?: [number, number];
  /** Associations: the correlation and its interval. */
  r?: number;
  r_ci?: [number, number];
  /** The Benjamini-Hochberg q of the result's family. */
  q?: number;
  /**
   * A tail prior's window test (brief/v4): the units at or below (low) or at or above (high) the cut against the rest,
   * the cut the input's lower or upper quartile over the whole span; rate_out is then the tail's rate. A both tail
   * (2026-10-07) reads the units at or below cut_low or at or above cut_high against the middle half between them, and
   * carries no cut. Absent for a window prior, whose test reads its band.
   */
  tail?: "low" | "high" | "both";
  /** A one-sided tail's cut, in the condition's unit; absent for a both tail. */
  cut?: number;
  /** A both tail's lower cut: the input's lower quartile over the whole span, in the condition's unit. */
  cut_low?: number;
  /** A both tail's upper cut: the input's upper quartile over the whole span, in the condition's unit. */
  cut_high?: number;
  /** A window test's display bins over the input, lowest first: where the rate rises is read from them. */
  bins?: BriefBin[];
}

/** One display bin of a window test: the input from lo to hi, the units in it, and their pooled rate (if pooled). */
export interface BriefBin {
  lo: number;
  hi: number;
  n: number;
  rate?: number;
  /** The fifth's pooled castings and defective ones (brief/v7), where the run stored them. */
  castings?: number;
  defective?: number;
}

/**
 * The part of a driver's input its fifths suggest (brief/v5): from the fifth of the lowest rate, the adjacent fifths
 * nearer the lowest rate than the highest, kept when the worst fifth's rate minus theirs is clear of zero. lo or hi is
 * absent where the range reaches the data's own end (no limit shown there). suggested false: why says why none. A
 * suggestion is the data's, never a limit: the process engineer sets the plant's (the Limits page).
 */
export interface BriefRange {
  suggested: boolean;
  lo?: number;
  hi?: number;
  /** The range's pooled rate over its n units, and the worst fifth's. */
  rate?: number;
  worst_rate?: number;
  n?: number;
  /** The input's fifths, lowest first. */
  bins: BriefBin[];
  why?: string;
  /**
   * The check that kept a suggested range (brief/v7): the fifths it pools, the one of the lowest rate it grew from and
   * the worst (counting from 0), its pooled castings and defective ones, the worst fifth's rate minus its own with the
   * interval at the check's level (and the level that holds over every pair of fifths), on castings divided by phi
   * (where phi came from). Absent on a range that suggests none, and in an older brief.
   */
  from_bin?: number;
  to_bin?: number;
  lowest_bin?: number;
  worst_bin?: number;
  castings?: number;
  defective?: number;
  difference_lo?: number;
  difference_hi?: number;
  level?: number;
  family_level?: number;
  phi?: number;
  phi_source?: string;
  /**
   * The analysis result the range was read from, the driver's headline row (result_ids holds it among the other
   * methods' rows), and its family (brief/v7): a window saved from the range names it, and the page links to its pair
   * on the Plan tab.
   */
  result_id?: string;
  family?: string;
  /** A suggested range's recommendation key (brief/v9): its parameter, and its grade where tested per grade. */
  recommendation_key?: string;
}

export interface BriefLag {
  min_s: number;
  max_s: number;
  source: string;
  confidence: string;
}

export interface BriefProvenance {
  /**
   * direct: measured on the heats or lots themselves; lot_window: a station's readings in the lag window before the
   * pour (confirmed when the plant confirmed every lag on the way); plant_day: the plant day's readings against the
   * day's castings (no lag is modelled, so never confirmed).
   */
  path: "direct" | "lot_window" | "plant_day";
  lag?: BriefLag;
  confirmed: boolean;
  /** How a lag-window input was joined mould by mould (brief/v14); absent for a window join. The path stays lot_window. */
  join?: BriefUnitJoin;
}

/**
 * A lag-window driver's input joined unit by unit: each mould matched to the pour it went into by count, n moulds along
 * the line, and what the check against the castings booked showed (it cannot see an error in n, and passing it does
 * not confirm the lag). note says it in plain words, and every number in it is a fact the driver keys (join.n,
 * join.count_lo, ...). parts_left_out are the parts whose lots were left out of this join.
 */
export interface BriefUnitJoin {
  n: number;
  count_min: number;
  count_max: number;
  parts_checked: number;
  parts_blind: number;
  parts_left_out?: string[];
  note: string;
}

export interface BriefDriver {
  hypothesis_id: string;
  mechanism_id: string;
  condition: BriefCondition;
  test: BriefTest;
  result_ids: string[];
  /**
   * The grade the evidence was tested in, where a plant that records grades has no whole-plant result for it:
   * stratum is its key ("grade:G-2"), stratum_label its words ("grade G-2"). Absent for the whole plant.
   */
  stratum?: string;
  stratum_label?: string;
  grain: "lot" | "heat" | "day";
  /** The support: the units tested (lots, heats or plant days, by grain). */
  n: number;
  /** The effective count the test rests on (a window test's distinct plant days, an autocorrelation-adjusted n). */
  n_eff?: number;
  /** The input's distinct values, not units. */
  n_distinct: number;
  effect: BriefEffect;
  provenance: BriefProvenance;
  flags: { exploratory: boolean; low_power: boolean; sign_agrees: boolean | null };
  verdict: Exclude<BriefVerdict, "untestable">;
  /**
   * Why the driver is not supported whatever its q, in plain words: its input's negative controls fired and its row
   * is exploratory, so the association is set aside (results/v9). Absent otherwise.
   */
  set_aside?: string;
  /**
   * A question the person can send the consultant, which may propose a change for them to approve. A tail's watch
   * names its cut (a both tail's, its two). note says what to know before acting on it: the condition stands in for
   * an input the plant does not record (brief/v4). recommendation_key keys it whatever its words (brief/v9).
   */
  action?: {
    kind: "alarm_rule" | "correlation_group" | "report" | "data";
    ask: string;
    /**
     * A watch's threshold (brief/v19), in the org's display units as served, its words in `ask` with it: below names
     * hi, above lo, both and outside each. Absent on a comparison and on a watch that names no number.
     */
    watch?: BriefWatch;
    note?: string;
    recommendation_key?: string;
  };
  /** The range the input's fifths suggest, on a supported or suggestive driver whose row stores them (brief/v5). */
  range?: BriefRange;
  /**
   * The fact of each number the driver states, by what it is (n, n_eff, rate_in, rate_out, excess_pieces,
   * excess_pieces.low, excess_pieces.high, r, q; cut (a both tail's cut_low and cut_high) and bin.<i>.*; range.lo,
   * range.hi, range.rate, range.worst_rate, range.n and range.bin.<i>.*): the page looks a number up by its key, never
   * by rebuilding one.
   */
  fact_keys: Record<string, string>;
}

export interface BriefUntestable {
  hypothesis_id: string;
  mechanism_id: string;
  /** What is missing, in the plant's words (a parameter, a connection's lag). */
  missing: string[];
  reason: BriefBlockReason;
  /** The analysis's names of the inputs a no_input item asks the plant to record (brief/v8). */
  inputs?: string[];
}

export interface BriefDefect {
  defect: string;
  label: string;
  pieces: number;
  share: number;
  /** The part of this defect the process data can speak to: the lots inside the process window; null with no process data. */
  in_process_window: { pieces: number; lots: number } | null;
  why_it_matters: string;
  mechanisms: BriefMechanism[];
  drivers: BriefDriver[];
  untestable: BriefUntestable[];
  /** A card after the largest defects by pieces, given because the data bears out one of its drivers (supported). */
  beyond_top?: boolean;
}

/** A watch's threshold (brief/v19): its side and ends, in `unit`. */
export interface BriefWatch {
  side: "below" | "above" | "both" | "outside";
  lo?: number;
  hi?: number;
  unit: string;
}

export interface BriefDataAsk {
  ask: string;
  /** The taxonomy defects it would let the plant test. */
  unlocks: string[];
  /** The scrap pieces of those defects in the lots window. */
  pieces_at_stake: number;
  reason: BriefBlockReason;
  /** How a foundry records the inputs an ask to record names (brief/v8); absent for another ask, and before. */
  record?: BriefRecording[];
  /** The ask's recommendation key, from the inputs it names and its reason (brief/v9). */
  recommendation_key?: string;
}

/**
 * How a foundry records some of an ask's inputs (the foundry playbook's recording): their labels, in the plant's
 * words, and the ways to record them, the usual way first.
 */
export interface BriefRecording {
  inputs: string[];
  how: string[];
}

/**
 * A period's shifts side by side (cadence/v12), summed from its shifts' roll-ups: descriptive, never a ranking. Facts
 * shifts.missing, and per row shift.<key>.shifts, .heats, .cond.<i>.n and .out, and with scrap .cast, .scrap, .rate and
 * .defect.<defect>.pieces.
 */
export interface CadenceShiftSplit {
  /** A lot of the period was booked on a shift. False where none was (a plant that books per day, or no lot booked). */
  scrap_by_shift: boolean;
  /** The conditions that left their window on any shift, in the order every row counts them. Counts only: no unit. */
  conditions: { condition: BriefCondition; entity: string; grade?: string; count_unit: string }[];
  /** The shifts the roll-ups hold in the period, in the calendar's day order. */
  rows: CadenceShiftRow[];
  /** The shift periods the calendar makes that the roll-ups hold no row for: the split is partial when above zero. */
  missing: number;
}

/** One shift of the calendar over a period. cast, scrap and rate only where the plant books its lots per shift. */
export interface CadenceShiftRow {
  label: string;
  key: string;
  shifts: number;
  heats: number;
  conditions: { n: number; out: number }[];
  cast?: number;
  scrap?: number;
  rate?: number;
  top: { defect: string; label: string; pieces: number }[];
}

/**
 * One of the overall brief's data asks as it stands in a period (cadence/v9): the defects it would let the plant test
 * that the period scrapped, their pieces in the period and their sum (the fact ask.<n>.pieces, n its place from 1).
 */
export interface CadenceDataAsk {
  ask: string;
  reason: BriefBlockReason;
  defects: { defect: string; label: string; pieces: number }[];
  pieces: number;
  record?: BriefRecording[];
  /** The overall brief's key for the ask (cadence/v11). */
  recommendation_key?: string;
}

export interface BriefFact {
  key: string;
  value: number;
  unit: string;
  text: string;
}

/**
 * The scrap's cost over by_month's months (brief/v13): each scrapped casting at its part's own cost per rejected casting,
 * else the plant's, in the plant's currency. A month's priced_scrap are the scrapped castings it could price; the rest
 * are left out of its cost and counted. Every sum is whole units of the currency and a fact: money.scrap.cost and
 * money.scrap.cost.<YYYY-MM> ("USD 124,500").
 */
export interface BriefScrapCost {
  currency: string;
  by_month: { period: string; scrap: number; priced_scrap: number; cost: number }[];
  total: number;
  scrap: number;
  priced_scrap: number;
  /** How a casting is priced, in words. */
  basis: string;
}

/**
 * One improved change's saving (platform-core twin/savings.go): the drop in the defect rate the effect check read, times
 * the castings made since the change at each part's cost per rejected casting; saved_low at the least drop its interval
 * allows. fact_keys names each number's fact (saved, low, castings, priced, rate_before, rate_after).
 */
export interface SavingLine {
  key: string;
  /** The words the person saw when they marked it tried. */
  text: string;
  /** The part the change was scoped to; absent for the whole plant. */
  part?: string;
  parts: string[];
  defects: string[];
  label: string;
  /** The change's plant day. */
  since: string;
  /** The last plant day counted. */
  through: string;
  /** Its after window is still open: the saving reads "so far". */
  so_far: boolean;
  rate_before: number;
  rate_after: number;
  castings: number;
  priced_castings: number;
  saved: number;
  saved_low: number;
  /**
   * Where the effect check read it against the parts that did not get the change (B3 T3): the fall the money counts,
   * beyond the others' own, and the others' own change of rate (below zero a fall), each a fraction.
   */
  comparison?: { fall: number; others_change: number };
  fact_keys: Record<string, string>;
}

/** An improved change left out of the sum, and why in the platform's words. */
export interface SavingNotCounted {
  key: string;
  text: string;
  since: string;
  kinds: ("overlap" | "baseline_drift" | "rescoped" | "unpriced")[];
  why: string;
}

/**
 * What the plant's improved changes saved, in money, read when served (beside the overall brief and the action list,
 * never stored): the counted lines most saved first, their sum since the earliest of them, the castings and those
 * priced, the lines not counted, the caveats in words and a fact for every number. `why` says why no money is shown
 * (no currency, no cost): the rest is then empty.
 */
export interface Savings {
  currency: string;
  since?: string;
  saved: number;
  saved_low: number;
  castings: number;
  priced_castings: number;
  lines: SavingLine[];
  not_counted: SavingNotCounted[];
  caveats: string[];
  facts: BriefFact[];
  why?: string;
}

/**
 * The gap a brief leads with (B1b T6; brief/v15 and cadence/v13; Go: internal/twin/brief_gap.go): the stretch it reads
 * against the best comparable one (basis months: the latest three plant months against the best three in a row before
 * them; periods: a period against the best quarter of the periods before it), split into mix (which parts were made)
 * and rate (how much each part scrapped; mix + rate = gap), and the defects whose own rates widened it, heaviest first
 * by weight (pieces, or cost once priced). At or below its best (gap not above nought, brief/v18) the brief states only
 * the two stretches' figures as facts, and the page says the stretch is lower than its best, or the same.
 */
export interface BriefGap {
  basis: "months" | "periods";
  latest: BriefGapStretch;
  best: BriefGapStretch;
  gap: number;
  mix: number;
  rate: number;
  defects: BriefGapDefect[];
  more: number;
  /** The rate gap the listed defects do not hold: those past them, those whose rates fell, and scrap with no defect. */
  rest: number;
  weight: "pieces" | "cost";
}

/** One of a gap's stretches: its plant days, its periods (and, for a period's best, of how many), castings and rate. */
export interface BriefGapStretch {
  from: string;
  to: string;
  periods: number;
  of?: number;
  keys?: string[];
  cast: number;
  scrap: number;
  rate: number;
}

/** A defect of the rate gap: its rate in each stretch, its own rate effect, and the pieces it is worth. */
export interface BriefGapDefect {
  defect: string;
  label: string;
  rate: number;
  best_rate: number;
  effect: number;
  pieces: number;
}

export interface PlantBrief {
  run: { id: string; done_at: string; stats_version: string };
  /**
   * What the lots' dates are where they are not the pour's (brief/v15, B1b T3): the org's lot_date_kind, inspected or
   * shipped. Absent for poured, the default (lib/brief/lot-dates).
   */
  lot_dates?: "inspected" | "shipped";
  windows: { lots: BriefLotsWindow; process: BriefProcessWindow | null };
  scrap: {
    /** What the Pareto is ranked by (brief/v16): money where the plant priced its scrap, in `currency`. */
    ranked_by?: "money" | "pieces";
    currency?: string;
    cast: number;
    scrap: number;
    /** scrap / cast; null when nothing was cast (never 0). */
    rate: number | null;
    pareto: BriefParetoRow[];
    by_year: BriefPeriod[];
    by_month: BriefPeriod[];
    /** The parts that carry the Pareto's largest defects (brief/v11); absent before it, and when no lot names a part. */
    defect_parts?: BriefDefectParts[];
    /** What the brief leads with (brief/v15): the latest three months against the best three in a row before them. */
    gap?: BriefGap;
    /** The latest year to date against the same span of the year before (brief/v12); absent where none. */
    to_date?: BriefToDate;
    /** What the scrap cost by month (brief/v13); absent where nothing is priced, and before it. */
    cost?: BriefScrapCost;
    /**
     * The scrap booked under the plant's words its vocabulary puts outside the foundry's own process (brief/v19:
     * machining, handling, finishing, stock counts), one row apart from the Pareto, whose defects never count it.
     * `scrap` and `rate` include it. Absent where no such word booked a piece, and before brief/v19.
     */
    outside?: BriefOutsideScrap;
  };
  changes: BriefChange[];
  defects: BriefDefect[];
  data_asks: BriefDataAsk[];
  facts: BriefFact[];
  /** The defects the plant holds at or under its own scrap targets, which have no card (brief/v6); absent before it. */
  under_target?: BriefUnderTarget[];
  /** Served beside the document, never stored: each recommendation's latest event, by its key (L1). */
  recommendation_states?: RecommendationStates;
  /**
   * Served beside the document, never stored: the plant's standing verdict on each recommendation, by its key (L2): a
   * collapsed one is folded (the overall brief) or left out (a period's); a reopened one shows its verdict as a note.
   */
  recommendation_verdicts?: RecommendationVerdicts;
  /** Served beside the document, never stored: what the plant's improved changes saved, in money; absent for none. */
  savings?: Savings;
}

/**
 * A defect the plant holds at or under the scrap target it set (brief/v6): its pieces and its rate over the lots
 * window's castings (facts pareto.<defect>.rate and .target), and the target. The brief covers every other defect.
 */
export interface BriefUnderTarget {
  defect: string;
  label: string;
  pieces: number;
  rate: number;
  target: number;
}

/** The cadences a CadenceBrief is built at. The overall brief is a PlantBrief. */
export type BriefCadence = "shift" | "day" | "week" | "month" | "year";

/**
 * A cadence brief as the view reads it: a cadence's, or a span's, the brief of a window someone chose (the AI report on
 * the brief engine, POST /v1/twin/briefs/span). A span has no tab, list or email of its own.
 */
export type BriefViewCadence = BriefCadence | "span";

/** A rate against its control limits; "none" where it has none (too few baseline periods, or nothing cast). */
export type BriefSignal = "above" | "below" | "within" | "none";

/**
 * A stretch of plant time: the instants [from, to) (RFC 3339), the plant days it covers (YYYY-MM-DD, both ends
 * included), and how many periods of its cadence it holds (1 for a window; the baseline's count).
 */
export interface BriefSpan {
  from: string;
  to: string;
  first_day: string;
  last_day: string;
  periods: number;
  /**
   * A baseline's kind: "moving" (the periods just before, the default) or "pinned" (a stretch the plant marked
   * stable, phase II). Absent on a window.
   */
  kind?: "moving" | "previous" | "pinned";
}

/** Whether the period's data is all in: every stream it needs reaches the period's end. */
export interface BriefDataState {
  complete: boolean;
  /** RFC 3339: how far the data reaches. */
  through?: string;
  /** The streams whose data stops before the period's end, in the plant's words. */
  late: string[];
  /**
   * The shifts that usually book and had not booked on partial_day, the scrap book's latest plant day, and the shifts
   * that had (cadence/v13): the day, and the period it falls in, are partial, and its figures are of the shifts booked
   * so far. Absent on a whole day.
   */
  partial_day?: string;
  missing_shifts?: string[];
  booked_shifts?: string[];
  /**
   * "none" when the period's roll-up holds no heat and no reading (cadence/v7): its conditions went unmeasured,
   * rather than holding their windows. Absent otherwise.
   */
  process?: "none";
}

/** Control limits for a rate at this period's castings: Laney p' or a p chart, from a phase-II baseline. */
export interface BriefLimits {
  low: number;
  high: number;
  method: string;
}

/** One defect's pieces in the period: its rate over the period's castings against its baseline. */
export interface CadenceParetoRow {
  defect: string;
  label: string;
  pieces: number;
  rate: number | null;
  baseline_rate: number | null;
  limits: BriefLimits | null;
  signal: BriefSignal;
  /** The defect's rate the plant's process engineer set (the Limits page), and the rate against it (cadence/v5). */
  target?: number;
  target_signal?: "above" | "within";
  /** Where the Pareto is ranked by money (cadence/v14): what its pieces cost; absent where none could be priced. */
  cost?: number;
  /** Where the Pareto is ranked by money: the pieces it could price. */
  priced_pieces?: number;
}

/**
 * Scrap booked outside the foundry's own process (brief/v19, cadence/v18): the plant's words for it, their pieces, and
 * their share of the scrap (facts outside.pieces and outside.share).
 */
export interface BriefOutsideScrap {
  words: string[];
  pieces: number;
  share: number;
}

/** The period's scrap against its baseline. */
export interface CadenceScrap {
  cast: number;
  scrap: number;
  /** scrap / cast; null when nothing was cast (never 0). */
  rate: number | null;
  baseline_rate: number | null;
  limits: BriefLimits | null;
  signal: BriefSignal;
  /** The total scrap rate the plant set, and the rate against it: above, or within (at or below it). */
  target?: number;
  target_signal?: "above" | "within";
  /** What the Pareto is ranked by (cadence/v14): money where the plant priced its scrap, in `currency`. */
  ranked_by?: "money" | "pieces";
  currency?: string;
  pareto: CadenceParetoRow[];
  /**
   * The period's lines side by side, each against its own limits (cadence/v13): where its lots name two lines or
   * more. The plant's rate pools every line and part, so a line that moves can hide in it. Absent otherwise.
   */
  lines?: CadenceLineRow[];
  /** The period's scrap outside the foundry's own process, one row apart from the Pareto (cadence/v18). */
  outside?: BriefOutsideScrap;
}

/** One line's castings and scrap in the period, its rate against its own baseline and limits (cadence/v13). */
export interface CadenceLineRow {
  /** The plant's own word for the line. */
  line: string;
  cast: number;
  scrap: number;
  rate: number | null;
  baseline_rate: number | null;
  limits: BriefLimits | null;
  signal: BriefSignal;
}

/** A playbook mechanism with this plant's standing on it, from the overall brief. */
export interface CadenceMechanismRef {
  id: string;
  title: string;
  verdict: BriefVerdict | "not_tested";
  /** The condition keys the mechanism's hypotheses test. */
  conditions: string[];
}

/** A defect or a part whose rate left its limits in the period. */
export interface CadenceMover {
  /** A line whose rate left its own limits is a mover too (cadence/v13). */
  kind: "defect" | "part" | "line";
  key: string;
  label: string;
  pieces: number;
  rate: number | null;
  baseline_rate: number | null;
  limits: BriefLimits | null;
  signal: "above" | "below";
  /** A defect's playbook mechanisms, strongest standing first. */
  mechanisms: CadenceMechanismRef[];
  /** Indexes into the brief's excursions whose condition one of those mechanisms names. */
  excursions: number[];
}

/** A process condition outside its window in the period. */
export interface CadenceExcursion {
  condition: BriefCondition;
  entity: string;
  band?: [number, number];
  /**
   * A window with one end (the plant's floor or ceiling): its end. The floor when the excursion went low, the ceiling
   * when it went high. Never with band.
   */
  bound?: number;
  /** The grade whose window the plant set, when the window is one grade's. */
  grade?: string;
  /**
   * The window: a textbook window in the priors, the plant's own (its process engineer set it on the Limits page), the
   * grade's aim window, or the condition's own control limits.
   */
  source: "prior_window" | "plant_window" | "grade_aim" | "control_limits";
  direction: "low" | "high" | "both";
  /**
   * What count and of count: a tag's readings, a heat's ladles, or heats (a condition the plant set a window on and
   * records once per heat); absent in a document composed before it.
   */
  count_unit?: "readings" | "ladles" | "heats";
  /** Readings, ladles or heats outside the window, of those in the period. */
  count: number;
  of: number;
  /** Minutes outside: a reading stream's (a ladle count has none). */
  minutes?: number;
  worst?: number;
  /** The taxonomy defects whose mechanisms name this condition. */
  defects_at_risk: string[];
  /**
   * The plant's usual share outside the same window at the same station over the period's baseline, and the share's
   * limits at `of` (cadence/v4). signal reads count/of against them: above is more than usual (news: only it carries
   * risks and actions), within or below is as usual, none is no usual yet. All absent in a brief composed before them.
   */
  baseline_share?: number;
  limits?: BriefLimits | null;
  signal?: "above" | "within" | "below" | "none";
}

/** A defect put at risk by an excursion: what was made under it, and what to check. */
export interface CadenceRisk {
  defect: string;
  label: string;
  mechanism_id: string;
  /** An index into the brief's excursions. */
  excursion: number;
  parts: string[];
  heats: number;
  check: string[];
}

/** A notable item of a period below this one: a mover or an excursion of one of its days or shifts. */
export interface CadenceChildItem {
  cadence: BriefCadence;
  period_key: string;
  kind: "mover" | "excursion";
  key: string;
  label: string;
  signal?: BriefSignal;
}

/**
 * Something to do, from the playbook's actions for a mechanism behind a defect above the plant's target, a mover or an
 * excursion: the groups, in that order (cadence/v5). excursion is the index of the excursion above the plant's usual
 * that the mechanism's condition ran in the period: the one that made it lead, or the one it came from.
 */
export interface CadenceAction {
  text: string;
  defect: string;
  mechanism_id: string;
  /** Why it is listed: above the plant's target, moved, a process excursion, or any other defect booked (cadence/v6). */
  source: "target" | "mover" | "excursion" | "defect";
  excursion?: number;
  /** The action's recommendation key, built from the stable id the playbook gives it (cadence/v11). */
  recommendation_key?: string;
}

/** The brief of one closed period at a cadence, or of a window someone chose (twin.CadenceBrief). */
export interface CadenceBrief {
  cadence: BriefViewCadence;
  /**
   * The period on the plant clock, one URL path segment: "2026-08-18.1" (shift "1" of that plant day), "2026-08-18",
   * "2026-W34" (the ISO week of the plant day), "2026-08", "2026".
   */
  period_key: string;
  window: BriefSpan;
  /** The stretch the period is compared with; null before any baseline period exists. */
  baseline: BriefSpan | null;
  data: BriefDataState;
  /** What the lots' dates are where they are not the pour's (cadence/v13): inspected or shipped; absent for poured. */
  lot_dates?: "inspected" | "shipped";
  /** What the brief leads with (cadence/v13): the period against the best quarter of the periods before it. */
  gap?: BriefGap;
  /**
   * null where the period has no outcomes: a shift of a plant that books its scrap per production day. A plant that
   * books its lots per shift gives its shifts their own (cadence/v10), against the same shift on the days before.
   */
  scrap: CadenceScrap | null;
  movers: CadenceMover[];
  excursions: CadenceExcursion[];
  risks: CadenceRisk[];
  children: CadenceChildItem[];
  actions: CadenceAction[];
  /**
   * The data that would sharpen this period's brief (cadence/v9): the overall brief's asks weighed by the period's own
   * scrapped pieces, the most at stake first. Absent in a brief composed before; the page reads it as none.
   */
  data_asks?: CadenceDataAsk[];
  /**
   * The period's machines (cadence/v16, E3): one entry per machine with a stored summary of the period, worst first
   * (unread, outside, drifted, steady), at most 10 (`machines_more` counts the rest), each with at most 3 items in its
   * outside (the largest share first), drifted (a significant slope first, then the most signals) and normal lists
   * (`outside_more`, `drifted_more`, `normal_more` count the rest; unread is never capped). Each number is a fact keyed by the entry's place and the item's, never by a port or an id:
   * machine.<i>.days, machine.<i>.outside.<j>.share, machine.<i>.normal.<j>.change, machine.<i>.normal_more and the
   * like. A key value that is unread is listed in unread only: the brief drops its items from outside, drifted and
   * normal before the cap. Absent where no machine has one, and in a brief composed before.
   */
  machines?: MachineEntry[];
  /** How many machines the brief does not list, the least bad after the 10 (fact machine.more); absent when none. */
  machines_more?: number;
  /**
   * A span's casting lots the import held as booked (their scrap above their castings, on a day with none, or
   * negative), not counted in its scrap: stated so the report never drops them silently. Facts held.lots and
   * held.scrap_pieces.
   */
  held?: { lots: number; scrap_pieces: number };
  /** The period's shifts side by side (cadence/v12): a week's, a month's and a span's, at a plant with a shift calendar. */
  shifts?: CadenceShiftSplit;
  /** The analysis run the mechanisms' standing comes from (the overall brief's). */
  run?: PlantBrief["run"];
  facts: BriefFact[];
  /** Served beside the document, never stored: each recommendation's latest event, by its key (L1). */
  recommendation_states?: RecommendationStates;
  /**
   * Served beside the document, never stored: the plant's standing verdict on each recommendation, by its key (L2): a
   * collapsed one is folded (the overall brief) or left out (a period's); a reopened one shows its verdict as a note.
   */
  recommendation_verdicts?: RecommendationVerdicts;
  /**
   * A week's only, served beside the document, never stored: the week's record in the reader's own view (D5,
   * brief_week_record.go). Absent at every other cadence.
   */
  record?: BriefWeekRecord;
  /** A week's only: the record did not read, the brief served without it. */
  record_unread?: boolean;
  /**
   * Every period's brief, served beside the document, never stored and never in its facts (step 6, #1273): the casting
   * holds whose cause falls in the period, counted by state, their heats by disposition, and each in brief (capped; the
   * counts are over every hold of the period).
   */
  casting_holds?: BriefCastingHolds;
  /** The period's casting holds did not read, the brief served without them. */
  casting_holds_unread?: boolean;
}

/** A period's casting holds in a brief (#1273). */
export interface BriefCastingHolds {
  suggested: number;
  confirmed: number;
  dismissed: number;
  closed: number;
  /** The heats of its held and closed holds by what was done with them; held = not yet disposed of. */
  heats: { held: number; released: number; scrapped: number; reworked: number };
  items: { id: string; state: "suggested" | "confirmed" | "dismissed" | "closed"; label: string; day: string; heats_n: number }[];
}

/** A view a reader gets of the findings or the questions: everything, what the plant's members see, or nothing. */
export type BriefRecordView = "internal" | "customer" | "hidden";

/**
 * The week's record (D5: the weekly brief is the plant's one weekly record): the parts of the weekly digest a brief
 * does not hold, read as of the week's end in the reader's own view. The consultant's summary states none of it.
 */
export interface BriefWeekRecord {
  /** The org's data streams, live tags and import sources, by kind and then name. */
  streams: BriefRecordStream[];
  /** The view of the findings: internal (a platform admin), customer (a member, the plant's knowledge shared), hidden. */
  findings_view: BriefRecordView;
  /** The findings whose status moved in the week, oldest first; none in the hidden view. */
  status_changes: BriefRecordChange[];
  /** The view of the questions: internal (a platform admin), customer (anyone else), hidden (the vendor overlay). */
  questions_view: BriefRecordView;
  /** The questions asked or answered in the week, and those open at its end, in the order they were asked. */
  questions: BriefRecordQuestion[];
  /** How many actions were overdue at the week's end (B3 T1); absent from an older server. */
  overdue_count?: number;
  /** The first 20 of them, oldest due first. */
  overdue_actions?: BriefRecordOverdue[];
}

/** An action overdue at the week's end: due by its last day, still open then. */
export interface BriefRecordOverdue {
  key: string;
  text_shown: string;
  /** The owner's display name, else "a teammate"; empty for none. */
  owner_name: string;
  due: string;
}

export interface BriefRecordStream {
  kind: "tag" | "import";
  /** The plant's name for it, as a person says it: a tag's parameter on its machine. */
  label: string;
  /** A tag's stored name, served to the platform's staff alone. */
  name?: string;
  /** The last plant day it holds data for, up to the week's end; absent when none. */
  through?: string;
  /** The rows it wrote in the week. */
  week_rows: number;
}

export interface BriefRecordChange {
  /** What the finding is about: its tag, else its defect, else its kind. */
  finding: string;
  kind: string;
  /** Absent for a finding's first status. */
  from?: string;
  to: string;
  at: string;
  /** The plant day of the move. */
  day: string;
  by: "engine" | "brain" | "user" | "plant";
}

export interface BriefRecordQuestion {
  topic: string;
  priority: "blocks_import" | "blocks_analysis" | "good_to_know";
  /** Where it stands now. */
  status: string;
  asked_on?: string;
  answered_on?: string;
  /** Asked before the week's end, not answered by it, and not withdrawn since. */
  open_at_end: boolean;
}

/** One brief in a cadence's list (GET /v1/twin/briefs). */
export interface BriefListItem {
  cadence: BriefViewCadence | "overall";
  period_key: string;
  first_day: string;
  last_day: string;
  generated_at: string;
  /** Set when late data recomposed the brief after it was first written. */
  revised_at: string | null;
  signal: BriefSignal;
  /**
   * The period's castings; null where it books no scrap (a shift of a plant that books per day), absent from a server
   * before 2026-09-28.
   */
  cast?: number | null;
}

export interface BriefList {
  items: BriefListItem[];
}

/** One sentence of the consultant's summary, and the keys of the brief's facts its numbers come from ([] for none). */
export interface BriefNarrativeSentence {
  text: string;
  facts: string[];
}

/**
 * The consultant's summary of a brief: a headline, then either 1 to 4 paragraphs of 1 to 6 sentences (v1), or cards
 * (v2, from 2026-10-06: docs/plans/2026-10-06_summary-cards.md), never both. As the page reads it (briefNarrativeOf),
 * paragraphs is always a list, empty on a summary of cards, and cards is there only on a summary the server sent cards
 * for.
 */
export interface BriefNarrative {
  headline: BriefNarrativeSentence;
  paragraphs: BriefNarrativeSentence[][];
  cards?: BriefNarrativeCard[];
}

/**
 * A card's kind, which decides the group of the page it falls in: what happened (happened, how the period stood;
 * mover, a defect or part outside its limits; risk, a condition outside its window and the castings it put at risk;
 * held, a defect held under the plant's target; data, the period's data late or incomplete), why (why, a mechanism and
 * how strongly this plant's data bears it out), what to do (action, one of the brief's actions; check, a playbook check
 * on the floor), and what would settle it (ask, a data ask and the pieces at stake).
 */
export type BriefNarrativeCardKind = "happened" | "mover" | "risk" | "held" | "data" | "why" | "action" | "check" | "ask";

/** Every kind of card, in the order of the page's groups. */
export const BRIEF_NARRATIVE_CARD_KINDS: readonly BriefNarrativeCardKind[] = [
  "happened",
  "mover",
  "risk",
  "held",
  "data",
  "why",
  "action",
  "check",
  "ask",
];

/** A why card's standing on its mechanism: the brief's own, never more or less (the platform checks it). */
export type BriefNarrativeCardVerdict = BriefVerdict | "not_tested";

const CARD_VERDICTS: readonly BriefNarrativeCardVerdict[] = ["supported", "suggestive", "not_supported", "untestable", "not_tested"];

/**
 * A card's one large number: the key of a fact the card cites, which the page shows in the brief's own words, and a
 * short label with no number ("of the castings").
 */
export interface BriefNarrativeCardFigure {
  fact: string;
  label: string;
}

/** The keys of what a card is about, each one the brief's document holds: Ask and "See in brief" read them. */
export interface BriefNarrativeCardAbout {
  defect?: string;
  mechanism?: string;
  condition?: string;
  part?: string;
  /** The recommendation's key, as the action list keys it: an action card's state comes from it. */
  recommendation?: string;
}

/**
 * One card of a summary written as cards: one idea, of a kind, with a short title (at most 90 characters, its numbers
 * checked like any sentence's), 1 to 3 sentences, and optionally a figure, what it is about, and on a why card (and
 * only there) the brief's standing on the mechanism it explains.
 */
export interface BriefNarrativeCard {
  kind: BriefNarrativeCardKind;
  title: BriefNarrativeSentence;
  figure?: BriefNarrativeCardFigure;
  sentences: BriefNarrativeSentence[];
  about?: BriefNarrativeCardAbout;
  verdict?: BriefNarrativeCardVerdict;
}

/**
 * A summary written for an earlier version of its brief (migration 0192): kept when the brief was composed again,
 * shown labelled as such and never as the brief's summary. Its figures are the text of the facts it cited as they were
 * then, by key (none for a summary written before 0192 kept them).
 */
export interface BriefNarrativeEarlier {
  narrative: BriefNarrative;
  /** RFC 3339: when it was written. */
  written_at?: string;
  figures?: Record<string, string>;
}

/**
 * A brief's summary as the page reads it: ready (the narrative, written for the brief as it now is), pending (armed or
 * being written), failed (message says so in the platform's words), or none (no summary for the brief as it now is).
 * earlier is the last summary written for an earlier version of the brief, where one was kept.
 */
export interface BriefNarrativeView {
  status: "ready" | "pending" | "failed" | "none";
  narrative?: BriefNarrative;
  /** RFC 3339: when the narrative was stored (ready only). */
  written_at?: string;
  /** The text of each fact the narrative cites, by key, as the brief stated it when it was written (ready only). */
  figures?: Record<string, string>;
  /** Why there is none, in the platform's words (failed only). */
  message?: string;
  /** RFC 3339: when a person asked for it (pending only, while the ask is open). */
  asked_at?: string;
  earlier?: BriefNarrativeEarlier;
  /**
   * false when the platform serves no summaries at all (the brain's worker token is not set, so the routes are not
   * mounted): nobody can ask for one. The page's own mark, never the server's.
   */
  served?: boolean;
}

/** The brief a summary is of: a cadence's, a span's, or the overall brief's (cadence overall, period all). */
export type BriefNarrativeCadence = BriefViewCadence | "overall";

/** A Go nil slice arrives as null: every list the page reads is read as a list. */
const list = <T>(v: T[] | null | undefined): T[] => v ?? [];

/** A cadence brief as the page reads it: its nil slices as empty lists. */
export function cadenceBriefOf(raw: CadenceBrief): CadenceBrief {
  return {
    ...raw,
    data: { ...raw.data, late: list(raw.data.late) },
    scrap: raw.scrap ? { ...raw.scrap, pareto: list(raw.scrap.pareto) } : null,
    movers: list(raw.movers).map((m) => ({
      ...m,
      mechanisms: list(m.mechanisms).map((x) => ({ ...x, conditions: list(x.conditions) })),
      excursions: list(m.excursions),
    })),
    excursions: list(raw.excursions).map((e) => ({ ...e, defects_at_risk: list(e.defects_at_risk) })),
    risks: list(raw.risks).map((r) => ({ ...r, parts: list(r.parts), check: list(r.check) })),
    children: list(raw.children),
    actions: list(raw.actions),
    data_asks: list(raw.data_asks).map((a) => ({ ...a, defects: list(a.defects), record: list(a.record) })),
    facts: list(raw.facts),
    record: raw.record
      ? {
          ...raw.record,
          streams: list(raw.record.streams),
          status_changes: list(raw.record.status_changes),
          questions: list(raw.record.questions),
        }
      : undefined,
  };
}

/** A cadence's list as the page reads it. */
export function briefListOf(raw: BriefList): BriefList {
  return { items: list(raw.items) };
}

export const PLANT_BRIEF_KEY = ["plant-brief"] as const;
export const BRIEF_LIST_KEY = ["brief-list"] as const;
export const CADENCE_BRIEF_KEY = ["cadence-brief"] as const;

/** The list reads at most this many briefs of a cadence, newest first. */
export const BRIEF_LIST_LIMIT = 60;

const isLiveApiFlag = import.meta.env.VITE_USE_LIVE_API === "true";

/** Live API, a signed-in person and an active org: the brief is asked for at all. */
export function useBriefLive(): boolean {
  const { isAuthenticated } = useAuth();
  const { activeOrgId } = useOrg();
  return isLiveApiFlag && isAuthenticated && activeOrgId !== null;
}

/** The org's plant time (zone, shifts, production day), read where the brief is: the summary's date is on its clock. */
export function useBriefPlantTimeQuery() {
  const live = useBriefLive();
  return useQuery({ queryKey: ["plant-time"], queryFn: plantTimeApi.get, enabled: live, staleTime: 5 * 60_000 });
}

/** The brief; `enabled` lets the sidebar ask only where the twin pack is on (one cached read serves both). */
export function usePlantBriefQuery(enabled = true) {
  const live = useBriefLive();
  return useQuery({
    queryKey: PLANT_BRIEF_KEY,
    queryFn: () => api.get<PlantBrief>("/v1/twin/analysis/brief"),
    enabled: live && enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * A cadence's briefs, newest first. `enabled` lets the sidebar ask only where the twin pack is on; the page and the
 * sidebar's new-brief mark share the read.
 */
export function useBriefListQuery(cadence: BriefViewCadence, enabled = true) {
  const live = useBriefLive();
  return useQuery({
    queryKey: [...BRIEF_LIST_KEY, cadence, BRIEF_LIST_LIMIT],
    queryFn: () =>
      api
        .get<BriefList>(`/v1/twin/briefs?cadence=${encodeURIComponent(cadence)}&limit=${BRIEF_LIST_LIMIT}`)
        .then(briefListOf),
    enabled: live && enabled,
    staleTime: 60_000,
  });
}

/**
 * Why a window's summary was not asked for (migration 0192): the org is past its cap on asks for a summary, so the span
 * is stored and shown with its summary as it was. error is the cap's code, detail the platform's words, retry_after the
 * seconds until an ask may be taken.
 */
export interface SpanSummaryNotAsked {
  error: string;
  detail: string;
  retry_after: number;
}

/**
 * The brief of a window of plant days (both inclusive), as the AI report asks for it: the stored day, week, month or
 * year brief when the window is exactly that period (composed false), else a span brief composed, stored and its
 * summary armed as the person's ask, within the org's cap on asks (past it, summary_not_asked says why: the cap holds
 * the summary, never the brief).
 */
export interface SpanBriefRef {
  cadence: BriefViewCadence;
  period_key: string;
  composed: boolean;
  summary_not_asked?: SpanSummaryNotAsked;
}

export async function requestSpanBrief(from: string, to: string): Promise<SpanBriefRef> {
  return api.post<SpanBriefRef>("/v1/twin/briefs/span", { from, to });
}

/**
 * What the AI report says when the platform will not brief the window, in its words: the window reaches days the
 * roll-ups do not hold, the plant has none yet, or the dates do not read. The cap on asks never refuses a window (it
 * holds only the summary, summaryNotAskedText); its refusals read as an ask's do, should the route send one.
 */
export function spanBriefErrorText(code: string | undefined): string {
  switch (code) {
    case "span_outside_rollups":
      return "The briefs cover the plant's closed days from its first rolled-up day: choose closed days inside that stretch.";
    case "no_rollups":
      return "The plant has no briefs yet: they start once its data has been rolled up by day.";
    case "invalid_span":
      return "Choose a window of plant days, the first on or before the last, at most 400 days long.";
    default:
      return briefAskRefusalText(code) ?? "The brief for this window could not be made just now. Try again.";
  }
}

/**
 * What the AI report says when a window's summary was not asked for, past the org's cap: that it was not, why in the
 * page's words, and when to ask again (the platform's words for a reason the page does not know).
 */
export function summaryNotAskedText(n: SpanSummaryNotAsked): string {
  const why = briefAskRefusalText(n.error, n.retry_after);
  return why
    ? `The consultant's summary of this window was not asked for. ${why}`
    : `The consultant's summary of this window was not asked for: ${n.detail}.`;
}

/** A period's brief's cache entry: its query's key, and what a re-ask of a span invalidates. */
export function cadenceBriefKey(cadence: BriefViewCadence, periodKey: string | null) {
  return [...CADENCE_BRIEF_KEY, cadence, periodKey] as const;
}

/** One period's brief, once a period is chosen. A closed period's brief changes only when late data revises it. */
export function useCadenceBriefQuery(cadence: BriefViewCadence, periodKey: string | null, enabled = true) {
  const live = useBriefLive();
  return useQuery({
    queryKey: cadenceBriefKey(cadence, periodKey),
    queryFn: () =>
      api
        .get<CadenceBrief>(`/v1/twin/briefs/${encodeURIComponent(cadence)}/${encodeURIComponent(periodKey ?? "")}`)
        .then(cadenceBriefOf),
    enabled: live && enabled && periodKey !== null,
    staleTime: 5 * 60_000,
  });
}

export const BRIEF_NARRATIVE_KEY = ["brief-narrative"] as const;

/** While the consultant writes a summary, the page reads it again this often. */
export const BRIEF_NARRATIVE_POLL_MS = 60_000;

/**
 * A window's summary a person is waiting on (the AI report) is read again this often: the brain claims it at once and
 * writes it in about a minute (a span is due at once, brief_narratives.go).
 */
export const SPAN_NARRATIVE_POLL_MS = 10_000;

/**
 * A person's ask is read every SPAN_NARRATIVE_POLL_MS for this long after it was asked, then every
 * BRIEF_NARRATIVE_POLL_MS: the brain writes one in about a minute, but a hand-back (its usage limit) can hold it an
 * hour or more.
 */
export const ASK_FAST_POLL_FOR_MS = 5 * 60_000;

const narrativeSentenceOf = (s: BriefNarrativeSentence): BriefNarrativeSentence => ({ text: s.text, facts: list(s.facts) });

const isRecordOf = (v: unknown): v is Record<string, unknown> => typeof v === "object" && v !== null && !Array.isArray(v);
const isText = (v: unknown): v is string => typeof v === "string" && v.trim() !== "";

const ABOUT_KEYS = ["defect", "mechanism", "condition", "part", "recommendation"] as const;

/**
 * One card as the page reads it, or null for one it cannot show: not a card, a kind it does not know (a later
 * server's), or no title. Its missing lists are empty; a figure is kept only with its fact and its label, an about only
 * with a key that is text, and a verdict only on a why card and only as one of the five standings.
 */
function narrativeCardOf(raw: unknown): BriefNarrativeCard | null {
  if (!isRecordOf(raw)) return null;
  const kind = raw.kind;
  if (typeof kind !== "string" || !(BRIEF_NARRATIVE_CARD_KINDS as readonly string[]).includes(kind)) return null;
  const title = raw.title;
  if (!isRecordOf(title) || !isText(title.text)) return null;
  const card: BriefNarrativeCard = {
    kind: kind as BriefNarrativeCardKind,
    title: narrativeSentenceOf(title as unknown as BriefNarrativeSentence),
    sentences: list(raw.sentences as BriefNarrativeSentence[] | null | undefined)
      .filter((s) => isRecordOf(s) && typeof s.text === "string")
      .map(narrativeSentenceOf),
  };
  const figure = raw.figure;
  if (isRecordOf(figure) && isText(figure.fact) && isText(figure.label)) card.figure = { fact: figure.fact, label: figure.label };
  const about = raw.about;
  if (isRecordOf(about)) {
    const keys: BriefNarrativeCardAbout = {};
    for (const k of ABOUT_KEYS) if (isText(about[k])) keys[k] = about[k] as string;
    if (Object.keys(keys).length > 0) card.about = keys;
  }
  if (kind === "why" && (CARD_VERDICTS as readonly unknown[]).includes(raw.verdict)) {
    card.verdict = raw.verdict as BriefNarrativeCardVerdict;
  }
  return card;
}

/**
 * A narrative as the page reads it: its nil slices as empty lists. A summary of paragraphs (v1) reads exactly as it
 * always did; one of cards (v2) has its cards, each read defensively (narrativeCardOf), and no paragraphs.
 */
const narrativeOf = (n: BriefNarrative): BriefNarrative => {
  const out: BriefNarrative = {
    headline: narrativeSentenceOf(n.headline),
    paragraphs: list(n.paragraphs).map((p) => list(p).map(narrativeSentenceOf)),
  };
  const raw: unknown = (n as { cards?: unknown }).cards;
  if (Array.isArray(raw) && raw.length > 0) {
    out.cards = raw.map(narrativeCardOf).filter((c): c is BriefNarrativeCard => c !== null);
  }
  return out;
};

/** A summary as the page reads it: its nil slices as empty lists, the earlier summary's too. */
export function briefNarrativeOf(raw: BriefNarrativeView): BriefNarrativeView {
  return {
    ...raw,
    ...(raw.narrative ? { narrative: narrativeOf(raw.narrative) } : {}),
    ...(raw.earlier?.narrative ? { earlier: { ...raw.earlier, narrative: narrativeOf(raw.earlier.narrative) } } : {}),
  };
}

/** The route of one brief's summary (GET reads it, POST asks for it). */
const narrativePath = (cadence: BriefNarrativeCadence, periodKey: string) =>
  `/v1/twin/briefs/${encodeURIComponent(cadence)}/${encodeURIComponent(periodKey)}/narrative`;

/**
 * The summary of one stored brief. A 404 is no summary: no brief is stored for that period (brief_not_found), or the
 * platform does not mount the route (the brain's worker token is not set: served false, and nobody can ask for one).
 * Any other failure is thrown.
 */
export async function fetchBriefNarrative(cadence: BriefNarrativeCadence, periodKey: string): Promise<BriefNarrativeView> {
  try {
    return briefNarrativeOf(await api.get<BriefNarrativeView>(narrativePath(cadence, periodKey)));
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) {
      const code = (err.body as { error?: unknown } | null)?.error;
      return code === "brief_not_found" ? { status: "none" } : { status: "none", served: false };
    }
    throw err;
  }
}

/**
 * A person asks for the summary of one stored brief (migration 0192): the summary as GET reads it, pending once the
 * ask armed it, ready when one was already written for the brief as it is. A refusal (429 past the org's cap on asks,
 * 404, 403) is thrown; askNarrativeErrorText says it in the page's words.
 */
export async function askBriefNarrative(cadence: BriefNarrativeCadence, periodKey: string): Promise<BriefNarrativeView> {
  return briefNarrativeOf(await api.post<BriefNarrativeView>(narrativePath(cadence, periodKey), {}));
}

/**
 * What the page says when an ask for a summary is refused, in its words: the plant's asks at the cap (a few open at
 * once, a day's worth, one per brief every few minutes; when to ask again where the server said), no such brief, or
 * no right to ask.
 */
export function askNarrativeErrorText(err: unknown): string {
  if (!(err instanceof ApiClientError)) return "The ask did not reach the platform. Try again.";
  const code = (err.body as { error?: unknown } | null)?.error;
  const capped = typeof code === "string" ? briefAskRefusalText(code, err.retryAfterS) : null;
  if (capped) return capped;
  if (code === "brief_not_found") return "This brief is no longer stored.";
  if (err.status === 403) return "Asking for a summary is for the people who author the plant's reports.";
  return "The summary could not be asked for just now. Try again.";
}

/**
 * The org's cap on asks for a summary in the page's words: a few open at once, a day's worth, one per brief every few
 * minutes, with when to ask again where it is known (retryAfterS, seconds); null for any other code.
 */
export function briefAskRefusalText(code: string | undefined, retryAfterS?: number | null): string | null {
  const wait = retryAfterS && retryAfterS > 0 ? waitWords(retryAfterS) : null;
  switch (code) {
    case "brief_ask_busy":
      return "The consultant is already writing five summaries this plant asked for. Ask again when one is done.";
    case "brief_ask_quota":
      return `This plant has asked for thirty summaries in the last day, its limit.${wait ? ` Ask again ${wait}.` : ""}`;
    case "brief_ask_again_soon":
      return `This summary was asked for a few minutes ago.${wait ? ` Ask again ${wait}.` : ""}`;
    default:
      return null;
  }
}

/** A wait in words: "in 7 minutes", "in about 4 hours". */
function waitWords(seconds: number): string {
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `in ${minutes} minute${minutes === 1 ? "" : "s"}`;
  const hours = Math.round(minutes / 60);
  return `in about ${hours} hour${hours === 1 ? "" : "s"}`;
}

/**
 * The poll: every minute while the summary is being written (every ms given, for a summary a person waits on); for one
 * a person asked for (its asked_at, else askedAt, when the page asked for the window), every SPAN_NARRATIVE_POLL_MS for
 * ASK_FAST_POLL_FOR_MS, then every minute, since a hand-back can hold it an hour or more; never once it is ready,
 * failed or none, before the first answer, or after a failed read (a failure is not retried in a loop).
 */
export function briefNarrativePollMs(
  state: { status: string; data?: BriefNarrativeView },
  ms: number = BRIEF_NARRATIVE_POLL_MS,
  askedAt?: string,
  now: number = Date.now(),
): number | false {
  if (state.status !== "success" || state.data?.status !== "pending") return false;
  const at = Date.parse(state.data.asked_at ?? askedAt ?? "");
  if (Number.isNaN(at)) return ms;
  return now - at < ASK_FAST_POLL_FOR_MS ? Math.min(ms, SPAN_NARRATIVE_POLL_MS) : Math.max(ms, BRIEF_NARRATIVE_POLL_MS);
}

/** A brief's summary's cache entry: its query's key, and what a re-ask of a span invalidates. */
export function briefNarrativeKey(cadence: BriefNarrativeCadence, periodKey: string | null) {
  return [...BRIEF_NARRATIVE_KEY, cadence, periodKey] as const;
}

/**
 * The summary's read: keyed by its brief's cadence and period, asked for once a period is chosen. askedAt is when the
 * page asked for the window, for the poll where the summary names no ask (briefNarrativePollMs).
 */
export function briefNarrativeQueryOptions(
  cadence: BriefNarrativeCadence,
  periodKey: string | null,
  enabled: boolean,
  pollMs: number = BRIEF_NARRATIVE_POLL_MS,
  askedAt?: string,
) {
  return queryOptions({
    queryKey: briefNarrativeKey(cadence, periodKey),
    queryFn: () => fetchBriefNarrative(cadence, periodKey ?? ""),
    enabled: enabled && periodKey !== null,
    staleTime: Math.min(60_000, pollMs),
    refetchInterval: (query) => briefNarrativePollMs(query.state, pollMs, askedAt),
  });
}

/**
 * The consultant's summary of one brief (overall, all for the overall brief); live only, like the briefs. pollMs is
 * how often a pending one is read again (SPAN_NARRATIVE_POLL_MS for a window a person waits on), and askedAt when the
 * page asked for that window.
 */
export function useBriefNarrativeQuery(
  cadence: BriefNarrativeCadence,
  periodKey: string | null,
  enabled = true,
  pollMs: number = BRIEF_NARRATIVE_POLL_MS,
  askedAt?: string,
) {
  const live = useBriefLive();
  return useQuery(briefNarrativeQueryOptions(cadence, periodKey, live && enabled, pollMs, askedAt));
}

/** The brief a summary is asked for. */
export interface NarrativeAskTarget {
  cadence: BriefNarrativeCadence;
  periodKey: string;
}

/**
 * A person's ask for a brief's summary (migration 0192): its answer is what the summary's read shows at once, and the
 * read then polls every 10 s while the summary is written (briefNarrativePollMs, asked_at). The mutation's variables
 * name the brief, so the page shows an ask's refusal only beside the brief it was for.
 */
export function useAskBriefNarrative() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (target: NarrativeAskTarget) => askBriefNarrative(target.cadence, target.periodKey),
    onSuccess: (view, target) => {
      qc.setQueryData(briefNarrativeKey(target.cadence, target.periodKey), view);
    },
  });
}

/*
 * The briefs' email (Foundry Intelligence, build order step 5; migration 0188): the roles each periodic cadence's new
 * briefs (shift, day, week, month, year) are emailed to, and whether the platform emails briefs at all. Every brief is
 * on the page whatever is chosen; the email is extra. The overall brief is never emailed: every analysis run rewrites
 * its one row, so it is no cadence here.
 *
 *   GET /v1/twin/briefs/email    any member: {platform_enabled, cadences: {cadence: [roles]}}
 *   PUT /v1/twin/briefs/email    org admins: {cadences}, the whole choice
 *     400 invalid_cadence, invalid_role, invalid_body   the choice is refused whole (detail names what)
 *     404 (the read)                                    this platform does not serve the briefs' email yet
 *
 * platform_enabled is the server's BRIEF_EMAIL_ENABLED, off by default: while it is off no brief is emailed whatever
 * the org chose, and a choice saved now waits for it. The Go mirror is platform-core/internal/twin/brief_email.go.
 */

/** A role a cadence's briefs may be emailed to: the org's roles (migration 0067). */
export type BriefEmailRole = "viewer" | "operator" | "technician" | "member" | "admin" | "owner";

/** A cadence whose briefs may be emailed: the five periodic ones, never the overall brief. */
export type BriefEmailCadence = BriefCadence;

/** The roles each cadence's briefs are emailed to ([] for nobody). */
export type BriefEmailCadences = Record<BriefEmailCadence, BriefEmailRole[]>;

export interface BriefEmailSettings {
  /** The server's BRIEF_EMAIL_ENABLED: while false, no brief is emailed whatever is chosen. */
  platform_enabled: boolean;
  cadences: BriefEmailCadences;
}

/** The settings as the server sends them: a Go nil list is null. */
interface BriefEmailSettingsWire {
  platform_enabled?: boolean | null;
  cadences?: Partial<Record<string, (string | null)[] | null>> | null;
}

const BRIEF_EMAIL_CADENCE_IDS: readonly BriefEmailCadence[] = ["shift", "day", "week", "month", "year"];
const BRIEF_EMAIL_ROLE_IDS: readonly BriefEmailRole[] = ["viewer", "operator", "technician", "member", "admin", "owner"];

/**
 * The settings as the page reads them: every periodic cadence, a null or missing list as nobody, roles once in their
 * order. Any other key (an overall cadence an older server sent) is not read.
 */
export function briefEmailSettingsOf(raw: BriefEmailSettingsWire): BriefEmailSettings {
  const cadences = {} as BriefEmailCadences;
  for (const c of BRIEF_EMAIL_CADENCE_IDS) {
    const given = new Set<string | null>(list(raw.cadences?.[c]));
    cadences[c] = BRIEF_EMAIL_ROLE_IDS.filter((r) => given.has(r));
  }
  return { platform_enabled: raw.platform_enabled === true, cadences };
}

export const BRIEF_EMAIL_KEY = ["brief-email"] as const;

/** The org's choice; null where the platform does not serve the route (404), so the page shows no control. */
export async function fetchBriefEmail(): Promise<BriefEmailSettings | null> {
  try {
    return briefEmailSettingsOf(await api.get<BriefEmailSettingsWire>("/v1/twin/briefs/email"));
  } catch (err) {
    if (err instanceof ApiClientError && err.status === 404) return null;
    throw err;
  }
}

/** Saves the org's whole choice (org admins) and reads back what the server stored. */
export async function saveBriefEmail(cadences: BriefEmailCadences): Promise<BriefEmailSettings> {
  return briefEmailSettingsOf(await api.put<BriefEmailSettingsWire>("/v1/twin/briefs/email", { cadences }));
}

/** The org's choice, read on the live platform only, like the briefs. */
export function useBriefEmailQuery() {
  const live = useBriefLive();
  return useQuery({ queryKey: BRIEF_EMAIL_KEY, queryFn: fetchBriefEmail, enabled: live, staleTime: 60_000 });
}
